import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Check, Trash2, X } from 'lucide-react';

/**
 * Key/value editor for the security parameters that ride along with every
 * question. Rows are local until Apply — nothing is sent while you type.
 */

let rowSeq = 0;
// Only ever a React key. crypto.randomUUID is absent outside a secure context,
// and an id generator is no reason for the settings panel to fail to render.
const nextRowId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `row-${(rowSeq += 1)}`);

const INPUT_CLASS =
  'w-full rounded-lg border border-line bg-surface-raised px-3 py-2 text-sm text-fg ' +
  'placeholder:text-fg-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-soft';

const COLUMN_LABEL_CLASS =
  'min-w-0 flex-1 text-[11px] font-semibold uppercase tracking-wide text-fg-muted';

export default function SecurityParamsSection({ securityParams, onApply, onClear }) {
  // Seeded once, on mount. SettingsModal unmounts this section while it is
  // closed, so reopening the modal re-reads whatever was last applied.
  const [rows, setRows] = useState(() => {
    // Values are coerced because these params are restored from localStorage,
    // where a hand-edited or older payload can hold a non-string — and the row
    // state is fed straight into controlled inputs and later trimmed.
    const initialRows = Object.entries(securityParams || {}).map(([key, value]) => ({
      id: nextRowId(),
      key,
      value: String(value ?? ''),
    }));
    // The editor always ends on an empty row, so there is somewhere to type
    // without hunting for an "add row" button first.
    initialRows.push({ id: nextRowId(), key: '', value: '' });
    return initialRows;
  });

  const handleChange = (id, field, newValue) => {
    setRows((prev) => {
      const updated = prev.map((row) => (row.id === id ? { ...row, [field]: newValue } : row));
      const lastRow = updated[updated.length - 1];
      if (lastRow.key || lastRow.value) {
        updated.push({ id: nextRowId(), key: '', value: '' });
      }
      return updated;
    });
  };

  const handleDeleteRow = (id) => {
    setRows((prev) => {
      const filtered = prev.filter((row) => row.id !== id);
      if (filtered.length === 0) {
        return [{ id: nextRowId(), key: '', value: '' }];
      }
      const lastRow = filtered[filtered.length - 1];
      if (lastRow.key || lastRow.value) {
        filtered.push({ id: nextRowId(), key: '', value: '' });
      }
      return filtered;
    });
  };

  const handleApply = () => {
    // Half-filled rows are someone mid-typing, not a parameter — they are
    // dropped rather than sent as an empty key or value.
    const params = {};
    rows.forEach((row) => {
      if (row.key.trim() && row.value.trim()) {
        params[row.key.trim()] = row.value.trim();
      }
    });
    onApply(params);
  };

  const handleClear = () => {
    setRows([{ id: nextRowId(), key: '', value: '' }]);
    onClear();
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-fg-muted">
        These pairs travel with every question you ask, so the agent can tell what you are allowed
        to see.
      </p>

      <div className="overflow-hidden rounded-xl border border-line bg-surface-sunken">
        <div
          className="security-params-scroll max-h-64 overflow-y-auto px-3 pb-3 pt-2"
          style={{
            // Reserve the gutter so the columns don't shift the moment a row
            // count crosses into scrolling.
            scrollbarGutter: 'stable',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--line-strong) transparent',
          }}
        >
          {/* Headers scroll with the rows they label, so they stay in the same
              column even once the body overflows. */}
          <div className="sticky top-0 z-10 flex items-center gap-2 bg-surface-sunken pb-2">
            <span className={COLUMN_LABEL_CLASS}>Key</span>
            <span className={COLUMN_LABEL_CLASS}>Value</span>
            <span className="w-8 shrink-0" aria-hidden="true" />
          </div>

          <div className="flex flex-col gap-2">
            {rows.map((row, index) => {
              const isTrailingEmpty = index === rows.length - 1 && !row.key && !row.value;
              const named = row.key.trim();
              return (
                <div key={row.id} className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <input
                      type="text"
                      value={row.key}
                      onChange={(e) => handleChange(row.id, 'key', e.target.value)}
                      placeholder="key"
                      autoComplete="off"
                      aria-label={`Parameter ${index + 1} key`}
                      className={`${INPUT_CLASS} ${isTrailingEmpty ? 'opacity-60' : ''}`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <input
                      type="text"
                      value={row.value}
                      onChange={(e) => handleChange(row.id, 'value', e.target.value)}
                      placeholder="value"
                      autoComplete="off"
                      aria-label={`Parameter ${index + 1} value`}
                      className={`${INPUT_CLASS} ${isTrailingEmpty ? 'opacity-60' : ''}`}
                    />
                  </div>
                  {isTrailingEmpty ? (
                    // A spacer, not a hidden button: there is nothing to remove
                    // on the trailing row, and a hidden button would still be a
                    // tab stop.
                    <span className="h-8 w-8 shrink-0" aria-hidden="true" />
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleDeleteRow(row.id)}
                      aria-label={named ? `Remove parameter ${named}` : `Remove parameter ${index + 1}`}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-fg-muted transition-colors hover:text-danger focus-visible:ring-2 focus-visible:ring-brand-soft"
                      style={{
                        transitionDuration: 'var(--d-fast)',
                        transitionTimingFunction: 'var(--ease-out)',
                      }}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 border-t border-line px-3 py-3">
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-line px-3 py-2 text-sm font-semibold text-fg transition-colors hover:border-line-strong hover:bg-surface-raised focus-visible:ring-2 focus-visible:ring-brand-soft"
            style={{
              transitionDuration: 'var(--d-fast)',
              transitionTimingFunction: 'var(--ease-out)',
            }}
          >
            <Trash2 size={14} aria-hidden="true" />
            Clear all
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-brand-fg transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-brand-soft"
            style={{
              transitionDuration: 'var(--d-fast)',
              transitionTimingFunction: 'var(--ease-out)',
            }}
          >
            <Check size={14} strokeWidth={2.5} aria-hidden="true" />
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

SecurityParamsSection.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  securityParams: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};
